/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {resolveListReadOnly} from '../../routing/adn-router-helper-core';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {ProductApiService, ProductModel} from './product-api.service';
import {ProductCopyModalComponent} from './product-copy-modal.component';
import {ProductEditPageComponent} from './product-edit-page.component';
import {ProductRoutePageComponent} from './product-route-page.component';

export function productStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ProductApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => resolveListReadOnly(false, 'products', permissions);
  const rh = adnRouterHelper.createRouterHelper('products.product', ProductApiService, 'app.adm', 'app/admin');
  const copyConfig = {
    modalComponent: ProductCopyModalComponent,
    stateUrl: 'app.adm.products.product',
    lcase: 'product',
    ucase: 'Product'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ProductRoutePageComponent}
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      productPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: ProductModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(
            model, true, readOnly(), api, 'Product', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Product', 'name', copyConfig
        ));
      },
      model: (productPage: SignalAdminResourcePageState<ProductModel>) => productPage.model()
    },
    views: {
      'content@app': {component: ProductEditPageComponent, bindings: {state: 'productPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'productPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'productPage'}}
    }
  }));
  return states.build();
}
